import { cn } from 'lib/helpers/utility';
import { useSendReceipt } from 'lib/hooks/queries/billing/useSendReceipt';
import { useReceiptDelivery } from 'lib/hooks/queries/billing/useReceiptDelivery';
import { RECEIPT_CHANNELS, isChannelUsed, isChannelAvailable } from 'lib/helpers/receiptDelivery';

import Button from 'components/shared/Button/Button';

const LABELS = {
  [RECEIPT_CHANNELS.EMAIL]: { send: 'Email', sent: 'Emailed', resend: 'Resend email' },
  [RECEIPT_CHANNELS.SMS]: { send: 'Text', sent: 'Texted', resend: 'Resend text' }
};

const ICONS = { [RECEIPT_CHANNELS.EMAIL]: 'mail-mono', [RECEIPT_CHANNELS.SMS]: 'message-mono' };

const ReceiptChannelButton = ({
  medium,
  params,
  delivery,
  isPatientSide,
  kioskDevice,
  isDisabled
}) => {
  const { mutate, isLoading } = useSendReceipt({ medium, params });
  const isUsed = isChannelUsed(delivery);
  const labels = LABELS[medium];

  // Patients (and the kiosk) send each channel once; staff may resend.
  const text = isUsed ? (isPatientSide ? labels.sent : labels.resend) : labels.send;

  return (
    <Button
      variant="link"
      size={kioskDevice ? 'large' : 'small'}
      text={text}
      icon={isUsed && isPatientSide ? 'tick-circle-mono' : ICONS[medium]}
      color="neutral"
      iconSize={kioskDevice ? 24 : 16}
      shade={700}
      iconShade={700}
      iconColor="neutral"
      className={cn('gap-1.5', kioskDevice && 'gap-2')}
      data-qa={medium === RECEIPT_CHANNELS.EMAIL ? 'email-to-patient' : 'text-to-patient'}
      onClick={() => mutate()}
      isLoading={isLoading}
      isDisabled={isDisabled || (isPatientSide && isUsed)}
    />
  );
};

/**
 * Email and Text receipt actions for the receipt sheet header. Each reflects the channel's
 * delivery state, including sends made elsewhere (staff, kiosk, automatic kiosk receipt).
 * `channels` (from receipt/get) hides a channel the backend would refuse; unknown shows both.
 */
const ReceiptSendActions = ({
  transactionId,
  appointmentId,
  token,
  channels,
  kioskDevice = false,
  isPatientSide = false,
  isDisabled = false
}) => {
  const delivery = useReceiptDelivery(transactionId, { listenToKiosk: kioskDevice });
  const params = { transactionId, appointmentId, token };
  const shared = { params, isPatientSide, kioskDevice, isDisabled };

  return (
    <>
      {isChannelAvailable(channels, RECEIPT_CHANNELS.EMAIL) && (
        <ReceiptChannelButton
          medium={RECEIPT_CHANNELS.EMAIL}
          delivery={delivery[RECEIPT_CHANNELS.EMAIL]}
          {...shared}
        />
      )}
      {isChannelAvailable(channels, RECEIPT_CHANNELS.SMS) && (
        <ReceiptChannelButton
          medium={RECEIPT_CHANNELS.SMS}
          delivery={delivery[RECEIPT_CHANNELS.SMS]}
          {...shared}
        />
      )}
    </>
  );
};

export default ReceiptSendActions;
